import React, {useCallback, useMemo} from 'react';
import {Button, Popover} from '@blueprintjs/core';
import {DatePicker} from '@blueprintjs/datetime';
import enUS from 'date-fns/locale/en-US';
import styled from 'styled-components';

import {StyledFormGroup} from './shared';


const MIN_DATE = new Date(2019, 0, 1);

const pad = (n, len = 2) => String(n).padStart(len, '0');

const toZulu = (d) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}Z`;

const toZuluDateOnly = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000Z`;

const SelectButton = styled(Button)`
    &[class*="bp"][class*="-button"] {
        border-radius: var(--sf-border-radius) !important;
        width: ${p => p.fill ? '100%' : 'fit-content'};
        justify-content: space-between;
        background: transparent !important;
        box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.2) !important;
    }
`;

const formatDisplay = (date, time) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const dateStr = d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
    if (!time) return dateStr;
    const timeOpts = {hour: '2-digit', minute: '2-digit', hour12: false};
    if (time.precision === 'second' || time.precision === 'millisecond') timeOpts.second = '2-digit';
    return dateStr + ' ' + d.toLocaleTimeString('en-GB', timeOpts);
};

const DatePickerField = ({label, value, disabled, error, time, fill, onChange}) => {
    const intent = error ? 'danger' : 'none';
    const maxDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 7); return d; }, []);
    const timeEnabled = time?.enabled === true;
    const precision = timeEnabled ? (time?.precision || 'minute') : undefined;

    const dateValue = useMemo(() => {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }, [value]);

    const handleChange = useCallback((selectedDate) => {
        if (!selectedDate) {
            onChange(undefined);
            return;
        }
        onChange(timeEnabled ? toZulu(selectedDate) : toZuluDateOnly(selectedDate));
    }, [onChange, timeEnabled]);

    const displayText = formatDisplay(value, timeEnabled ? {precision} : null) || 'Select\u2026';

    return (
        <StyledFormGroup label={label} helperText={error?.message} intent={intent}>
            <Popover
                disabled={disabled}
                minimal
                fill={!!fill}
                placement="bottom-start"
                content={
                    <DatePicker
                        locale={enUS}
                        value={dateValue}
                        onChange={handleChange}
                        showActionsBar={true}
                        highlightCurrentDay={true}
                        reverseMonthAndYearMenus={true}
                        minDate={MIN_DATE}
                        maxDate={maxDate}
                        timePrecision={precision}
                    />
                }
            >
                <SelectButton
                    text={displayText}
                    rightIcon="calendar"
                    disabled={disabled}
                    fill={fill}
                />
            </Popover>
        </StyledFormGroup>
    );
};

export {DatePickerField};
